<template>
  <div class="forecast-accuracy-tracker p-3">
    <!-- Header -->
    <div class="d-flex align-items-center justify-content-between mb-3">
      <div>
        <h4 class="mb-0">Forecast Accuracy Analyzer</h4>
        <small class="text-muted">
          Compare forecast accuracy across versions, lead days, and locations
          <template v-if="localMetadata">
            — <strong>{{ localMetadata.name }}</strong>
          </template>
        </small>
      </div>
      <div class="d-flex align-items-center gap-2">
        <button class="btn btn-sm btn-outline-secondary" @click="openLoadDefinitions"
          :disabled="!isSessionReady" title="Load saved datasets">
          <i class="bi bi-folder2-open me-1"></i>Load
        </button>
        <button class="btn btn-sm btn-outline-secondary" @click="openNewDefinition" title="New analysis">
          <i class="bi bi-plus-lg me-1"></i>New
        </button>
        <span v-if="!isSessionReady" class="badge bg-warning text-dark">
          <i class="bi bi-exclamation-triangle me-1"></i>Not signed in
        </span>
        <span v-else class="badge bg-success">
          <i class="bi bi-check-circle me-1"></i>Connected
        </span>
      </div>
    </div>

    <div class="row g-3">
      <!-- Left panel: Configuration -->
      <div class="col-lg-4 col-xl-3">
        <div class="card mb-3">
          <div class="card-header fw-semibold">
            <i class="bi bi-gear me-1"></i> Configuration
          </div>
          <div class="card-body">
            <!-- Version selector -->
            <div class="mb-3">
              <label class="form-label fw-medium">Forecast Version</label>
              <div class="btn-group w-100" role="group">
                <button type="button" class="btn btn-sm"
                  :class="version === 'v1' ? 'btn-primary' : 'btn-outline-primary'"
                  @click="version = 'v1'">V1</button>
                <button type="button" class="btn btn-sm"
                  :class="version === 'v2' ? 'btn-primary' : 'btn-outline-primary'"
                  @click="version = 'v2'">V2</button>
                <button type="button" class="btn btn-sm"
                  :class="version === 'v2ext' ? 'btn-primary' : 'btn-outline-primary'"
                  @click="version = 'v2ext'">V2EXT</button>
              </div>
            </div>

            <!-- Unit group -->
            <div class="mb-3">
              <label class="form-label fw-medium">Units</label>
              <select class="form-select form-select-sm" v-model="unitGroup">
                <option value="us">US (°F, mph, in)</option>
                <option value="metric">Metric (°C, km/h, mm)</option>
                <option value="uk">UK (°C, mph, mm)</option>
              </select>
            </div>

            <!-- Date range -->
            <div class="mb-3">
              <label class="form-label fw-medium">Verification Period</label>
              <div class="d-flex gap-2">
                <input type="date" class="form-control form-control-sm" v-model="startDate" placeholder="Start">
                <input type="date" class="form-control form-control-sm" v-model="endDate" placeholder="End">
              </div>
              <small class="text-muted">Dates must be in the past for verification</small>
            </div>

            <!-- Max lead days -->
            <div class="mb-3">
              <label class="form-label fw-medium">Max Lead Days</label>
              <input type="number" class="form-control form-control-sm" v-model.number="maxLeadDays" min="0" max="15">
              <small class="text-muted">Compare forecasts from 0 to N days ahead</small>
            </div>
          </div>
        </div>

        <!-- Locations -->
        <div class="card mb-3">
          <div class="card-header py-2 d-flex justify-content-between align-items-center">
            <h6 class="mb-0"><i class="bi bi-geo-alt me-2"></i>Locations <span class="badge bg-primary ms-1">{{ locations.length }}</span></h6>
            <div class="d-flex gap-1">
              <button class="btn btn-sm btn-outline-primary" @click="$refs.locationPanel.showPasteModal = true" title="Paste from clipboard">
                <i class="bi bi-clipboard"></i>
              </button>
              <button class="btn btn-sm btn-outline-primary" @click="$refs.locationPanel.showImportModal = true" title="Import from CSV">
                <i class="bi bi-upload"></i>
              </button>
              <button class="btn btn-sm btn-outline-primary" @click="$refs.locationPanel.showAddForm = !$refs.locationPanel.showAddForm; if ($refs.locationPanel.showAddForm) $nextTick(() => $refs.locationPanel.$refs.addLocationInput?.focus());" title="Add location">
                <i class="bi bi-plus-lg"></i>
              </button>
              <button v-if="locations.length > 0" class="btn btn-sm btn-outline-danger" @click="locations = []" title="Clear all">
                <i class="bi bi-trash"></i>
              </button>
            </div>
          </div>
          <div class="card-body py-2">
            <ExceptionLocationPanel
              ref="locationPanel"
              v-model="locations"
              label=""
              itemLabel="location"
              placeholder="Address or coordinates..."
              maxHeight="200px"
              :hideHeader="true"
            />
          </div>
        </div>

        <!-- Scoring Config -->
        <div class="card mb-3">
          <div class="card-header fw-semibold">
            <i class="bi bi-sliders me-1"></i> Scoring Thresholds
          </div>
          <div class="card-body">
            <div class="mb-2">
              <label class="form-label mb-0 small">Temp threshold (±{{ unitGroup === 'us' ? '°F' : '°C' }})</label>
              <input type="number" class="form-control form-control-sm" v-model.number="scoringConfig.tempThreshold" step="0.5" min="0">
            </div>
            <div class="mb-2">
              <label class="form-label mb-0 small">Wind threshold (±{{ unitGroup === 'us' ? 'mph' : 'km/h' }})</label>
              <input type="number" class="form-control form-control-sm" v-model.number="scoringConfig.windThreshold" step="1" min="0">
            </div>
            <div class="mb-2">
              <label class="form-label mb-0 small">Precip rain threshold ({{ unitGroup === 'us' ? 'in' : 'mm' }})</label>
              <input type="number" class="form-control form-control-sm" v-model.number="scoringConfig.precipRainThreshold" step="0.01" min="0">
            </div>
            <button v-if="hasResults" class="btn btn-sm btn-outline-secondary w-100 mt-2"
              @click="reEvaluateScores">
              <i class="bi bi-arrow-repeat me-1"></i>Re-evaluate Scores
            </button>
          </div>
        </div>

        <!-- Save & Run button -->
        <button class="btn btn-primary w-100 mb-2" @click="openSaveAndRun"
          :disabled="!canRun || isRunning">
          <span v-if="isRunning">
            <span class="spinner-border spinner-border-sm me-1" role="status"></span>
            {{ pollingStatus || 'Running...' }}
          </span>
          <span v-else>
            <i class="bi bi-play-fill me-1"></i>
            {{ localMetadata ? 'Re-run Analysis' : 'Save & Run Analysis' }}
          </span>
        </button>

        <!-- Save-only button (for updating config without running) -->
        <button v-if="localMetadata" class="btn btn-outline-secondary w-100 mb-3 btn-sm"
          @click="openSaveOnly" :disabled="!canSave || isRunning">
          <i class="bi bi-save me-1"></i>Save Config Only
        </button>

        <div v-if="runError" class="alert alert-danger py-2 small">
          <i class="bi bi-exclamation-triangle me-1"></i>{{ runError }}
        </div>
      </div>

      <!-- Right panel: Results -->
      <div class="col-lg-8 col-xl-9">
        <!-- No results yet -->
        <div v-if="!hasResults && !isRunning" class="card">
          <div class="card-body text-center text-muted py-5">
            <i class="bi bi-bar-chart-line" style="font-size: 3rem;"></i>
            <p class="mt-3">Configure your analysis and click <strong>Save &amp; Run Analysis</strong> to start.</p>
            <p class="small">The analyzer will compare forecast data against observations for each location,<br>
              computing MAE, Bias, RMSE, and ForecastWatch-style scores across lead days.</p>
          </div>
        </div>

        <!-- Loading results -->
        <div v-if="isLoadingResult" class="card mb-3">
          <div class="card-body text-center py-4">
            <span class="spinner-border spinner-border-sm me-2"></span>Loading results...
          </div>
        </div>

        <!-- Summary -->
        <div v-if="hasResults">
          <!-- Download bar -->
          <div class="d-flex justify-content-end gap-2 mb-2">
            <button class="btn btn-sm btn-outline-secondary" @click="downloadResultJson">
              <i class="bi bi-filetype-json me-1"></i>JSON
            </button>
            <button class="btn btn-sm btn-outline-secondary" @click="downloadResultCsv">
              <i class="bi bi-filetype-csv me-1"></i>CSV
            </button>
          </div>

          <!-- Summary cards -->
          <div class="row g-2 mb-3">
            <div class="col-sm-3">
              <div class="card text-center">
                <div class="card-body py-2">
                  <div class="text-muted small">Locations</div>
                  <div class="fs-4 fw-bold">{{ summary?.locationCount || 0 }}</div>
                </div>
              </div>
            </div>
            <div class="col-sm-3">
              <div class="card text-center">
                <div class="card-body py-2">
                  <div class="text-muted small">Comparisons</div>
                  <div class="fs-4 fw-bold">{{ summary?.totalComparisons || 0 }}</div>
                </div>
              </div>
            </div>
            <div class="col-sm-3">
              <div class="card text-center">
                <div class="card-body py-2">
                  <div class="text-muted small">Version</div>
                  <div class="fs-4 fw-bold">{{ summary?.version || '-' }}</div>
                </div>
              </div>
            </div>
            <div class="col-sm-3">
              <div class="card text-center">
                <div class="card-body py-2">
                  <div class="text-muted small">Max Lead Days</div>
                  <div class="fs-4 fw-bold">{{ summary?.maxLeadDays ?? '-' }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Tab navigation + location selector -->
          <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
            <ul class="nav nav-tabs mb-0">
              <li class="nav-item">
                <a class="nav-link" :class="{ active: activeTab === 'scores' }" href="#"
                  @click.prevent="activeTab = 'scores'">ForecastWatch Scores</a>
              </li>
              <li class="nav-item">
                <a class="nav-link" :class="{ active: activeTab === 'stats' }" href="#"
                  @click.prevent="activeTab = 'stats'">Error Statistics</a>
              </li>
              <li class="nav-item">
                <a class="nav-link" :class="{ active: activeTab === 'raw' }" href="#"
                  @click.prevent="activeTab = 'raw'">Raw JSON</a>
              </li>
            </ul>
            <select v-if="activeTab !== 'raw' && locationResults.length > 1"
              class="form-select form-select-sm" style="width: auto; min-width: 180px;"
              v-model="selectedLocation">
              <option value="__global__">All Locations (Global)</option>
              <option v-for="loc in locationResults" :key="loc.name" :value="loc.name">
                {{ loc.name || loc.resolvedAddress || 'Unknown' }}
              </option>
            </select>
          </div>

          <!-- Scores tab -->
          <div v-if="activeTab === 'scores'" class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <span class="fw-semibold">ForecastWatch-Style Scores
                <template v-if="isGlobalView">(Global)</template>
                <template v-else> — {{ selectedLocation }}</template>
              </span>
              <span v-if="clientScores && isGlobalView" class="badge bg-info">Client-evaluated</span>
            </div>
            <div class="card-body p-0">
              <div class="table-responsive">
                <table class="table table-sm table-hover mb-0">
                  <thead class="table-light">
                    <tr>
                      <th>Lead Day</th>
                      <th class="text-end">High Temp %</th>
                      <th class="text-end">Low Temp %</th>
                      <th class="text-end">Precip %</th>
                      <th class="text-end">Wind %</th>
                      <th class="text-end fw-bold">Composite %</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="ld in activeLeadDays" :key="ld">
                      <td>Day {{ ld }}</td>
                      <td class="text-end" :class="scoreClass(getScore(ld, 'highTempAccuracy'))">
                        {{ formatPct(getScore(ld, 'highTempAccuracy')) }}
                      </td>
                      <td class="text-end" :class="scoreClass(getScore(ld, 'lowTempAccuracy'))">
                        {{ formatPct(getScore(ld, 'lowTempAccuracy')) }}
                      </td>
                      <td class="text-end" :class="scoreClass(getScore(ld, 'precipAccuracy'))">
                        {{ formatPct(getScore(ld, 'precipAccuracy')) }}
                      </td>
                      <td class="text-end" :class="scoreClass(getScore(ld, 'windAccuracy'))">
                        {{ formatPct(getScore(ld, 'windAccuracy')) }}
                      </td>
                      <td class="text-end fw-bold" :class="scoreClass(getScore(ld, 'composite'))">
                        {{ formatPct(getScore(ld, 'composite')) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Stats tab -->
          <div v-if="activeTab === 'stats'" class="card">
            <div class="card-header fw-semibold">Error Statistics by Lead Day
              <template v-if="isGlobalView">(Global)</template>
              <template v-else> — {{ selectedLocation }}</template>
            </div>
            <div class="card-body p-0">
              <div v-for="ld in activeLeadDays" :key="'stat-'+ld" class="mb-3">
                <div class="px-3 pt-2">
                  <h6 class="text-primary mb-1">Lead Day {{ ld }}</h6>
                </div>
                <div class="table-responsive">
                  <table class="table table-sm table-hover mb-0">
                    <thead class="table-light">
                      <tr>
                        <th>Element</th>
                        <th class="text-end">Count</th>
                        <th class="text-end">MAE</th>
                        <th class="text-end">Bias</th>
                        <th class="text-end">RMSE</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(stats, element) in getDailyStats(ld)" :key="element">
                        <td>{{ element }}</td>
                        <td class="text-end">{{ stats.count }}</td>
                        <td class="text-end">{{ formatNum(stats.mae) }}</td>
                        <td class="text-end" :class="biasClass(stats.bias)">{{ formatNum(stats.bias, true) }}</td>
                        <td class="text-end">{{ formatNum(stats.rmse) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <!-- Raw JSON tab -->
          <div v-if="activeTab === 'raw'" class="card">
            <div class="card-header fw-semibold d-flex justify-content-between">
              <span>Raw JSON Output</span>
              <button class="btn btn-sm btn-outline-secondary" @click="copyJson">
                <i class="bi bi-clipboard me-1"></i>Copy
              </button>
            </div>
            <div class="card-body">
              <pre class="bg-light p-2 rounded-2 small" style="max-height: 500px; overflow: auto;">{{ formattedJson }}</pre>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== Save Modal ===== -->
    <div v-if="showSaveModal" class="modal d-block" tabindex="-1" style="background: rgba(0,0,0,0.5);">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ localMetadata ? 'Update & Run' : 'Save & Run Analysis' }}
            </h5>
            <button type="button" class="btn-close" @click="closeSaveModal"></button>
          </div>
          <div class="modal-body">
            <!-- Overwrite confirmation -->
            <div v-if="showConfirmOverwrite" class="alert alert-warning">
              <p class="mb-2">A dataset with this name already exists. Overwrite?</p>
              <div class="d-flex gap-2">
                <button class="btn btn-sm btn-warning" @click="confirmOverwrite">Overwrite</button>
                <button class="btn btn-sm btn-outline-secondary" @click="cancelOverwrite">Cancel</button>
              </div>
            </div>

            <!-- Name/description inputs (only before running) -->
            <template v-if="!pollingState.isRefreshingResults && !hasResultComplete && !hasResultError">
              <div class="mb-3">
                <label class="form-label">Name</label>
                <input type="text" class="form-control" v-model="saveDefnName" maxlength="120"
                  placeholder="e.g. ForecastAccuracy_v2_2026-04-06"
                  @keyup.enter="handleModalSave">
              </div>
              <div class="mb-3">
                <label class="form-label">Description <span class="text-muted">(optional)</span></label>
                <textarea class="form-control" v-model="saveDefnDesc" rows="2" maxlength="500"></textarea>
              </div>
              <div v-if="saveError" class="alert alert-danger py-2 small">{{ saveError }}</div>
            </template>

            <!-- Polling state -->
            <div v-if="pollingState.isRefreshingResults" class="text-center py-4">
              <div class="spinner-border text-primary mb-2" role="status"></div>
              <div class="text-muted">{{ pollingStatus || 'Checking status...' }}</div>
            </div>

            <!-- Error result -->
            <div v-if="hasResultError" class="alert alert-danger">
              <i class="bi bi-exclamation-triangle me-1"></i>
              {{ taskInstance?.info || 'Job failed' }}
            </div>

            <!-- Success result -->
            <div v-if="hasResultComplete && hasResults" class="alert alert-success">
              <i class="bi bi-check-circle me-1"></i>Analysis complete!
              <div class="mt-2 d-flex gap-2">
                <button class="btn btn-sm btn-outline-primary" @click="downloadResultJson">
                  <i class="bi bi-filetype-json me-1"></i>JSON
                </button>
                <button class="btn btn-sm btn-outline-primary" @click="downloadResultCsv">
                  <i class="bi bi-filetype-csv me-1"></i>CSV
                </button>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="closeSaveModal">
              {{ hasResults ? 'Close' : 'Cancel' }}
            </button>
            <button v-if="!pollingState.isRefreshingResults && !hasResultComplete && !hasResultError"
              class="btn btn-primary" @click="handleModalSave"
              :disabled="!saveDefnName.trim() || isSaving">
              <span v-if="isSaving">
                <span class="spinner-border spinner-border-sm me-1"></span>Saving...
              </span>
              <span v-else>
                <i class="bi bi-play-fill me-1"></i>{{ saveModalAction === 'run' ? 'Save & Run' : 'Save' }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ===== Load Definitions Modal ===== -->
    <div v-if="showLoadModal" class="modal d-block" tabindex="-1" style="background: rgba(0,0,0,0.5);">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Load Saved Dataset</h5>
            <button type="button" class="btn-close" @click="showLoadModal = false"></button>
          </div>
          <div class="modal-body">
            <div v-if="isLoadingDefinitions" class="text-center py-4">
              <span class="spinner-border spinner-border-sm me-2"></span>Loading...
            </div>
            <div v-else-if="savedDefinitions.length === 0" class="text-center text-muted py-4">
              No saved forecast accuracy datasets found.
            </div>
            <div v-else class="list-group">
              <a v-for="defn in savedDefinitions" :key="defn.id"
                class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                href="#" @click.prevent="handleSelectDefinition(defn)">
                <div>
                  <strong>{{ defn.name }}</strong>
                  <div v-if="defn.desc" class="small text-muted">{{ defn.desc }}</div>
                  <div v-if="defn.instance" class="small">
                    <span :class="defn.instance.status === 3 ? 'text-success' : defn.instance.status === 4 ? 'text-danger' : 'text-warning'">
                      {{ defn.instance.status === 3 ? 'Complete' : defn.instance.status === 4 ? 'Error' : 'Running' }}
                    </span>
                  </div>
                </div>
                <div class="d-flex align-items-center gap-2">
                  <button class="btn btn-sm btn-outline-danger" @click.stop.prevent="handleDeleteDefinition(defn)"
                    title="Delete">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </a>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="showLoadModal = false">Close</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useForecastAccuracy } from './useForecastAccuracy.js';
import ExceptionLocationPanel from '../weatherexceptions/core/ExceptionLocationPanel.vue';

export default defineComponent({
  name: 'ForecastAccuracyTracker',
  components: {
    ExceptionLocationPanel
  },
  props: {
    title: { type: String, default: 'Forecast Accuracy Analyzer' },
    serverRoot: { type: String, default: null }
  },
  setup(props) {
    const engine = useForecastAccuracy({
      storageKey: 'forecast-accuracy-tracker',
      serverRoot: props.serverRoot
    });

    onMounted(() => {
      engine.initSession();
    });

    // UI state
    const activeTab = ref('scores');
    const newLocation = ref('');
    const hasLocationPanel = ref(true);
    const showSaveModal = ref(false);
    const showLoadModal = ref(false);
    const saveModalAction = ref('run'); // 'run' or 'save'
    const selectedLocation = ref('__global__');

    // Auto-close save modal when poll completes (like FlexibleWeatherTracker)
    engine.setOnPollComplete((status) => {
      showSaveModal.value = false;
    });

    const isGlobalView = computed(() => selectedLocation.value === '__global__');

    // Find the selected location object
    const selectedLocationData = computed(() => {
      if (isGlobalView.value) return null;
      return engine.locationResults.value.find(l => l.name === selectedLocation.value) || null;
    });

    // Lead days — from global summary or selected location
    const sortedLeadDays = computed(() => {
      const stats = engine.summary.value?.globalLeadDayStats;
      if (!stats) return [];
      return Object.keys(stats).map(Number).sort((a, b) => a - b);
    });

    const activeLeadDays = computed(() => {
      if (isGlobalView.value) return sortedLeadDays.value;
      const loc = selectedLocationData.value;
      if (!loc?.leadDayStats) return [];
      return Object.keys(loc.leadDayStats).map(Number).sort((a, b) => a - b);
    });

    // Score accessors — global uses client scores if available; per-location uses server scores
    function getScore(leadDay, field) {
      if (isGlobalView.value) {
        // Try client-evaluated scores first
        if (engine.clientScores.value?.global?.[leadDay]) {
          const cs = engine.clientScores.value.global[leadDay];
          const mapping = {
            'highTempAccuracy': 'highTempAcc',
            'lowTempAccuracy': 'lowTempAcc',
            'precipAccuracy': 'precipAcc',
            'windAccuracy': 'windAcc',
            'composite': 'composite'
          };
          const val = cs[mapping[field] || field];
          if (val !== null && val !== undefined) return val;
        }
        const stats = engine.summary.value?.globalLeadDayStats?.[leadDay];
        if (!stats?.scores) return null;
        return stats.scores[field];
      } else {
        const loc = selectedLocationData.value;
        const lds = loc?.leadDayStats?.[leadDay];
        if (!lds?.scores) return null;
        return lds.scores[field];
      }
    }

    function getDailyStats(leadDay) {
      if (isGlobalView.value) {
        return engine.summary.value?.globalLeadDayStats?.[leadDay]?.dailyStats || {};
      } else {
        const loc = selectedLocationData.value;
        return loc?.leadDayStats?.[leadDay]?.dailyStats || {};
      }
    }

    // Formatting
    function formatPct(val) {
      if (val === null || val === undefined || !isFinite(val)) return '-';
      return val.toFixed(1) + '%';
    }

    function formatNum(val, showSign = false) {
      if (val === null || val === undefined || !isFinite(val)) return '-';
      if (showSign && val > 0) return '+' + val.toFixed(2);
      return val.toFixed(2);
    }

    function scoreClass(val) {
      if (val === null || val === undefined || !isFinite(val)) return '';
      if (val >= 80) return 'text-success';
      if (val >= 60) return 'text-warning';
      return 'text-danger';
    }

    function biasClass(val) {
      if (val === null || val === undefined || !isFinite(val)) return '';
      if (Math.abs(val) < 0.5) return 'text-success';
      if (Math.abs(val) < 2) return 'text-warning';
      return 'text-danger';
    }

    // Location actions
    function handleAddLocation() {
      if (newLocation.value) {
        engine.addLocation(newLocation.value.trim());
        newLocation.value = '';
      }
    }

    // === Save modal flow ===
    function openSaveAndRun() {
      engine.saveConfig();
      // Clear previous completion state so modal shows inputs, not old result
      engine.taskInstance.value = null;
      engine.results.value = null;
      engine.clientScores.value = null;
      engine.runError.value = null;
      if (!engine.saveDefnName.value) {
        engine.saveDefnName.value = engine.generateDefaultName();
      }
      saveModalAction.value = 'run';
      showSaveModal.value = true;
    }

    function openSaveOnly() {
      engine.saveConfig();
      saveModalAction.value = 'save';
      showSaveModal.value = true;
    }

    function closeSaveModal() {
      showSaveModal.value = false;
      engine.stopPolling();
    }

    async function handleModalSave() {
      if (saveModalAction.value === 'run') {
        await engine.saveAndRun();
      } else {
        await engine.saveOnly();
        if (!engine.saveError.value) {
          showSaveModal.value = false;
        }
      }
    }

    // === Load definitions flow ===
    function openLoadDefinitions() {
      showLoadModal.value = true;
      engine.loadSavedDefinitions();
    }

    function openNewDefinition() {
      engine.newDefinition();
    }

    async function handleSelectDefinition(defn) {
      showLoadModal.value = false;
      await engine.selectDefinition(defn);
    }

    async function handleDeleteDefinition(defn) {
      if (confirm('Delete "' + defn.name + '"?')) {
        await engine.deleteDefinition(defn);
      }
    }

    // JSON display
    const formattedJson = computed(() => {
      if (!engine.results.value) return '';
      try {
        return JSON.stringify(engine.results.value, null, 2);
      } catch (e) {
        return 'Error formatting JSON';
      }
    });

    function copyJson() {
      if (formattedJson.value) {
        navigator.clipboard.writeText(formattedJson.value);
      }
    }

    return {
      ...engine,
      activeTab,
      newLocation,
      hasLocationPanel,
      showSaveModal,
      showLoadModal,
      saveModalAction,
      selectedLocation,
      isGlobalView,
      selectedLocationData,
      sortedLeadDays,
      activeLeadDays,
      getScore,
      getDailyStats,
      formatPct,
      formatNum,
      scoreClass,
      biasClass,
      handleAddLocation,
      openSaveAndRun,
      openSaveOnly,
      closeSaveModal,
      handleModalSave,
      openLoadDefinitions,
      openNewDefinition,
      handleSelectDefinition,
      handleDeleteDefinition,
      formattedJson,
      copyJson
    };
  }
});
</script>

<style scoped>
.forecast-accuracy-tracker {
  max-width: 1400px;
  margin: 0 auto;
}
</style>
